/**
 * One traveller's time on the ground — where they are staying and what else they
 * have got on, between the flight in and the flight out.
 *
 * Wade, 2026-08-17: *"an ability to add hotel details for each with event showing
 * and like itins able to add other activity before/after."*
 *
 * This is the only band on the page that is per-PERSON, and deliberately so: a
 * hotel belongs to somebody. The flights above and below are grouped by
 * direction because they happen at a moment; a stay is a fact about a traveller.
 *
 * ⚠️ These rows are `itinerary_items` (026) — the same rows the traveller's own
 * trip page shows. Adding a hotel here puts it on their itinerary and their
 * print-out; it is not a manifest-only note that nobody else will ever see.
 */
import { Hotel, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ITEM_ICON, ITEM_LABEL, formatTime } from '@/components/trips/itinerary/itinerary-utils';
import type { ManifestItem, ManifestPerson } from '@/lib/api';
import type { ItineraryItemType } from '@/lib/types';
import { personLabel } from '@/pages/eventManifestView';
import { shortDate } from './ManifestLegRow';

export interface ManifestStayCardProps {
  person: ManifestPerson;
  items: ManifestItem[];
  /** The date to open the add dialog on — normally the meeting day. */
  defaultDate: string | null;
  onAdd?: (memberId: string, dayDate: string, itemType: ItineraryItemType) => void;
  onDelete?: (memberId: string, item: ManifestItem) => void;
  deletingId?: string | null;
}

export default function ManifestStayCard({
  person,
  items,
  defaultDate,
  onAdd,
  onDelete,
  deletingId,
}: ManifestStayCardProps) {
  const lodging = items.filter((i) => i.item_type === 'lodging');
  const other = items.filter((i) => i.item_type !== 'lodging');
  // A traveller with no trip row cannot hold an itinerary item. Say why rather
  // than showing a button that would fail.
  const canAdd = !!onAdd && !!person.member_id && !!person.trip_id && !!defaultDate;

  return (
    <div className="break-inside-avoid rounded-lg border border-border bg-card px-3 py-2.5 shadow-paper">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="text-sm font-semibold">{personLabel(person)}</span>
        {lodging.length === 0 && (
          <span className="rounded-full border border-border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            no hotel on file
          </span>
        )}
        {canAdd && (
          <div className="no-print ml-auto flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => onAdd!(person.member_id!, defaultDate!, 'lodging')}
              className="inline-flex items-center gap-1 rounded-full border border-dashed border-border px-2 py-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:border-[hsl(var(--primary))]/50 hover:bg-[hsl(var(--primary))]/[0.06] hover:text-[hsl(var(--accent-foreground))]"
            >
              <Hotel className="h-3 w-3" aria-hidden="true" />
              Add hotel
            </button>
            <button
              type="button"
              onClick={() => onAdd!(person.member_id!, defaultDate!, 'activity')}
              className="inline-flex items-center gap-1 rounded-full border border-dashed border-border px-2 py-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:border-[hsl(var(--primary))]/50 hover:bg-[hsl(var(--primary))]/[0.06] hover:text-[hsl(var(--accent-foreground))]"
            >
              <Plus className="h-3 w-3" aria-hidden="true" />
              Add activity
            </button>
          </div>
        )}
        {!!onAdd && !person.trip_id && (
          <span className="ml-auto text-[11px] italic text-muted-foreground">
            no trip yet — add their travel first
          </span>
        )}
      </div>

      {/* ⚠️ An empty stay is ONE line, not a block. Found by looking at an
          8-person event: "no hotel on file" as a badge AND "Nothing on the
          ground for X yet" as a sentence said the same thing twice, and four
          empty cards pushed "Heading home" off the screen. The badge above is
          the whole message. */}
      {items.length > 0 && (
        <ul className="mt-2 space-y-1.5">
          {[...lodging, ...other].map((it) => {
            const Icon = ITEM_ICON[it.item_type] ?? Hotel;
            const start = formatTime(it.start_time);
            const end = formatTime(it.end_time);
            const place = it.location || it.city;
            return (
              <li key={it.id} className="group flex items-start gap-2.5">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                  <Icon className="h-3 w-3" aria-hidden="true" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span className="text-sm font-medium text-foreground">{it.title}</span>
                    <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                      {ITEM_LABEL[it.item_type] ?? it.item_type}
                    </span>
                    {/* A confirmation number IS the booked state for an add-on
                        (035). Same emerald language as a booked flight, so the
                        page has one vocabulary for "this is real". */}
                    {it.confirmation ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white">
                        conf {it.confirmation}
                      </span>
                    ) : (
                      <span className="rounded-full border border-amber-400 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-amber-700">
                        no confirmation
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
                    {it.day_date && <span>{shortDate(it.day_date)}</span>}
                    {start && (
                      <span className="font-mono">
                        {start}
                        {end ? ` – ${end}` : ''}
                      </span>
                    )}
                    {place && <span>{place}</span>}
                    {it.cost !== null && <span className="font-mono">${it.cost}</span>}
                  </div>
                  {it.notes && (
                    <p className="mt-0.5 text-xs leading-snug text-muted-foreground">{it.notes}</p>
                  )}
                </div>
                {onDelete && person.member_id && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="no-print h-6 w-6 shrink-0 text-destructive opacity-50 transition-opacity hover:text-destructive group-hover:opacity-100 focus-visible:opacity-100"
                    aria-label={`Remove ${it.title} from ${person.display_name}'s itinerary`}
                    disabled={deletingId === it.id}
                    onClick={() => onDelete(person.member_id!, it)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
